import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, Plus, Trash2, ChevronLeft, AlertCircle } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetProduct, adminCreateProduct, adminUpdateProduct } from '../../services/api';

const CATEGORIES = ['suppressors', 'sme', 'clamp-ons', 'muzzle-devices'];
const USE_CASE_OPTIONS = ['Tactical', 'Hunting', 'Range', 'Home Defense', 'Long Range', 'Precision Shooting', 'Competition', '3-Gun', 'EDC', 'AK Platform', 'Truck Gun', 'SHTF'];

const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2.5 outline-none transition-colors placeholder-zinc-600";
const labelCls = "block font-inter text-xs text-zinc-400 uppercase tracking-wider mb-1.5";
const sectionCls = "bg-[#1a1a1a] border border-white/10 p-5 mb-4";

export default function AdminProductEditor() {
  const { id } = useParams();
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const isEdit = !!id && id !== 'new';

  const [form, setForm] = useState({
    name: '', subtitle: '', slug: '', category: 'suppressors',
    price: '', dealer_price: '', price_range: '', rating: '5.0', review_count: '0',
    badge: '', image: '', description: '', short_desc: '',
    in_stock: true, featured: false, nfa_item: false, status: 'active', shipping_note: '',
  });
  const [images, setImages] = useState(['']);
  const [specRows, setSpecRows] = useState([{ key: '', value: '' }]);
  const [features, setFeatures] = useState(['']);
  const [useCases, setUseCases] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isEdit) {
      adminGetProduct(token, id)
        .then(p => {
          setForm({
            name: p.name || '', subtitle: p.subtitle || '', slug: p.slug || '',
            category: p.category || 'suppressors', price: String(p.price || ''),
            dealer_price: p.dealer_price ? String(p.dealer_price) : '',
            price_range: p.price_range || '', rating: String(p.rating || 5),
            review_count: String(p.review_count || 0), badge: p.badge || '',
            image: p.image || '', description: p.description || '',
            short_desc: p.short_desc || '', in_stock: p.in_stock !== false,
            featured: !!p.featured, nfa_item: !!p.nfa_item, status: p.status || 'active',
            shipping_note: p.shipping_note || '',
          });
          setImages(p.images?.length ? p.images : ['']);
          setSpecRows(Object.entries(p.specs || {}).map(([key, value]) => ({ key, value })));
          if (!Object.keys(p.specs || {}).length) setSpecRows([{ key: '', value: '' }]);
          setFeatures(p.features?.length ? p.features : ['']);
          setUseCases(p.use_cases || []);
          setVariants(p.variants || []);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [id, token, isEdit]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const specs = {};
      specRows.forEach(r => { if (r.key.trim()) specs[r.key.trim()] = r.value; });
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        dealer_price: form.dealer_price ? parseFloat(form.dealer_price) : undefined,
        rating: parseFloat(form.rating) || 5,
        review_count: parseInt(form.review_count) || 0,
        images: images.filter(Boolean),
        specs,
        features: features.filter(Boolean),
        use_cases: useCases,
        variants: variants.filter(Boolean).length ? variants.filter(Boolean) : undefined,
      };
      if (isEdit) {
        await adminUpdateProduct(token, id, payload);
        setSuccess('Product updated successfully!');
      } else {
        const created = await adminCreateProduct(token, payload);
        setSuccess('Product created! Redirecting...');
        setTimeout(() => navigate(`/admin/products/${created._id}`), 1200);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="product-editor-form">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/products" className="text-zinc-500 hover:text-white">
            <ChevronLeft size={20} />
          </Link>
          <div>
            <h1 className="font-bebas text-3xl text-white tracking-wider">
              {isEdit ? 'EDIT PRODUCT' : 'NEW PRODUCT'}
            </h1>
            {isEdit && <p className="font-inter text-xs text-zinc-600">{form.slug}</p>}
          </div>
        </div>
        <button
          data-testid="save-product-button"
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
        >
          <Save size={14} />
          {saving ? 'Saving...' : 'Save Product'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">
          <AlertCircle size={15} />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3 mb-4">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Main fields */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Info */}
          <div className={sectionCls}>
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">BASIC INFORMATION</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Product Name *</label>
                <input data-testid="product-name-input" required className={inputCls} value={form.name} onChange={set('name')} placeholder="e.g. The Naughty 9" />
              </div>
              <div>
                <label className={labelCls}>Subtitle</label>
                <input className={inputCls} value={form.subtitle} onChange={set('subtitle')} placeholder="e.g. 9mm Ultra Compact Suppressor" />
              </div>
              <div>
                <label className={labelCls}>URL Slug</label>
                <input className={inputCls} value={form.slug} onChange={set('slug')} placeholder="auto-generated if empty" />
              </div>
              <div>
                <label className={labelCls}>Badge</label>
                <input className={inputCls} value={form.badge} onChange={set('badge')} placeholder="Best Seller, Featured, etc." />
              </div>
              <div>
                <label className={labelCls}>Shipping Note</label>
                <input className={inputCls} value={form.shipping_note} onChange={set('shipping_note')} placeholder="*Choose UPS Shipping at Checkout*" />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className={sectionCls}>
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">DESCRIPTION</h2>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Short Description</label>
                <input data-testid="product-short-desc" className={inputCls} value={form.short_desc} onChange={set('short_desc')} placeholder="One-line product summary" />
              </div>
              <div>
                <label className={labelCls}>Full Description</label>
                <textarea
                  data-testid="product-description"
                  rows={5}
                  className={`${inputCls} resize-none`}
                  value={form.description}
                  onChange={set('description')}
                  placeholder="Full product description..."
                />
              </div>
            </div>
          </div>

          {/* Specs */}
          <div className={sectionCls}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bebas text-lg text-white tracking-wider">SPECIFICATIONS</h2>
              <button type="button" onClick={() => setSpecRows(r => [...r, { key: '', value: '' }])} className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-orange-400">
                <Plus size={12} />Add Row
              </button>
            </div>
            <div className="space-y-2" data-testid="specs-editor">
              {specRows.map((row, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className={`${inputCls} flex-1`}
                    placeholder="Label (e.g. Caliber)"
                    value={row.key}
                    onChange={e => setSpecRows(rows => rows.map((r, j) => j === i ? { ...r, key: e.target.value } : r))}
                  />
                  <input
                    className={`${inputCls} flex-[2]`}
                    placeholder="Value (e.g. 9mm)"
                    value={row.value}
                    onChange={e => setSpecRows(rows => rows.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                  />
                  <button type="button" onClick={() => setSpecRows(rows => rows.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className={sectionCls}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bebas text-lg text-white tracking-wider">FEATURES</h2>
              <button type="button" onClick={() => setFeatures(f => [...f, ''])} className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-orange-400">
                <Plus size={12} />Add Feature
              </button>
            </div>
            <div className="space-y-2" data-testid="features-editor">
              {features.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className={`${inputCls} flex-1`}
                    placeholder="Feature description"
                    value={f}
                    onChange={e => setFeatures(arr => arr.map((v, j) => j === i ? e.target.value : v))}
                  />
                  <button type="button" onClick={() => setFeatures(arr => arr.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div className={sectionCls}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bebas text-lg text-white tracking-wider">IMAGES</h2>
              <button type="button" onClick={() => setImages(i => [...i, ''])} className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-orange-400">
                <Plus size={12} />Add Image
              </button>
            </div>
            <p className="font-inter text-xs text-zinc-600 mb-3">Enter image URLs (from media library or external). First image is the primary.</p>
            <div className="space-y-2" data-testid="images-editor">
              {images.map((img, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="w-12 h-12 bg-[#0f0f0f] border border-white/5 flex-shrink-0 overflow-hidden">
                    {img && <img src={img} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />}
                  </div>
                  <input
                    className={`${inputCls} flex-1`}
                    placeholder="https://... or /api/uploads/..."
                    value={img}
                    onChange={e => setImages(arr => arr.map((v, j) => j === i ? e.target.value : v))}
                  />
                  <button type="button" onClick={() => setImages(arr => arr.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Settings */}
        <div className="space-y-4">
          {/* Publishing */}
          <div className={sectionCls}>
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">PUBLISHING</h2>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Status</label>
                <select
                  data-testid="product-status-select"
                  className={inputCls}
                  value={form.status}
                  onChange={set('status')}
                >
                  <option value="active">Active (Visible)</option>
                  <option value="hidden">Hidden (Draft)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select
                  data-testid="product-category-select"
                  className={inputCls}
                  value={form.category}
                  onChange={set('category')}
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2 pt-1">
                {[
                  { key: 'featured', label: 'Featured on Homepage' },
                  { key: 'nfa_item', label: 'NFA Item (Suppressor)' },
                  { key: 'in_stock', label: 'In Stock' },
                ].map(toggle => (
                  <label key={toggle.key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      data-testid={`toggle-${toggle.key}`}
                      type="checkbox"
                      checked={form[toggle.key]}
                      onChange={set(toggle.key)}
                      className="w-4 h-4 accent-orange-500"
                    />
                    <span className="font-inter text-sm text-zinc-300">{toggle.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className={sectionCls}>
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">PRICING</h2>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Retail Price *</label>
                <input
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  className={inputCls}
                  value={form.price}
                  onChange={set('price')}
                  placeholder="299.00"
                />
              </div>
              <div>
                <label className={labelCls}>Dealer Price</label>
                <input
                  data-testid="product-dealer-price"
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputCls}
                  value={form.dealer_price}
                  onChange={set('dealer_price')}
                  placeholder="Wholesale price"
                />
              </div>
              <div>
                <label className={labelCls}>Price Range Display</label>
                <input
                  className={inputCls}
                  value={form.price_range}
                  onChange={set('price_range')}
                  placeholder="e.g. $99 – $114 (optional)"
                />
                <p className="font-inter text-[10px] text-zinc-600 mt-1">If set, shows instead of exact price</p>
              </div>
            </div>
          </div>

          {/* Use Cases */}
          <div className={sectionCls}>
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">USE CASES</h2>
            <div className="grid grid-cols-2 gap-2" data-testid="use-cases-editor">
              {USE_CASE_OPTIONS.map(uc => (
                <label key={uc} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCases.includes(uc)}
                    onChange={e => {
                      if (e.target.checked) setUseCases(prev => [...prev, uc]);
                      else setUseCases(prev => prev.filter(u => u !== uc));
                    }}
                    className="w-3.5 h-3.5 accent-orange-500"
                  />
                  <span className="font-inter text-xs text-zinc-400">{uc}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Primary Image URL (quick) */}
          <div className={sectionCls}>
            <h2 className="font-bebas text-lg text-white tracking-wider mb-4">PRIMARY IMAGE</h2>
            <input
              data-testid="product-image-input"
              className={inputCls}
              value={form.image}
              onChange={set('image')}
              placeholder="Primary image URL"
            />
            {form.image && (
              <div className="mt-2 bg-[#0f0f0f] border border-white/5 h-28 flex items-center justify-center overflow-hidden">
                <img src={form.image} alt="" className="max-h-full object-contain p-2" onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
